/**
 * Arabic copy for the ops page «التقويم» (tasks/multi-unit-availability-grid.md).
 *
 * Ops pages are Arabic-only (I18N_STRATEGY — only the crew's task screens are bilingual), so the
 * words live here, the way guestReviewLabels.js serves the reviews page. Night-state words are NOT
 * repeated here: they come from AVAILABILITY_VIEW (utils/availabilityUiHelpers.js), the vocabulary
 * the unit calendar uses, so the two can never drift. Numbers arrive already formatted.
 */

import ar from '../locales/ar';

/**
 * The Airbnb offer words in the night popup are the pricing card's own (locales/ar.js
 * `pricing.offers`), read in Arabic whatever the user's locale — this page is Arabic-only. Same
 * contract as useLanguage's `t`: a missing key comes back as the key itself.
 */
export const offersText = (key) => key.split('.').reduce((acc, part) => acc?.[part], ar) ?? key;

export const GRID_TEXT = {
  pageTitle: 'التقويم',
  intro: 'توفر كل الوحدات في صفحة واحدة — للعرض فقط. اضغط على أي ليلة لرؤية تفاصيلها وفتح تقويم الوحدة، حيث يتم التعديل.',
  today: 'اليوم',
  prev: 'الفترة السابقة',
  next: 'الفترة التالية',
  windowGroup: 'عدد الأيام',
  windowDays: (days) => `${days} يوماً`,
  filters: 'الفلاتر',
  filtersActive: (count) => `الفلاتر (${count})`,
  building: 'المبنى',
  allBuildings: 'كل المباني',
  status: 'حالة الوحدة',
  bedrooms: 'غرف النوم',
  search: 'بحث',
  searchPlaceholder: 'رمز الوحدة أو رقمها أو اسمها',
  freeOnly: 'فيها ليلة متاحة',
  resetFilters: 'مسح الفلاتر',
  // The legend is a key, not an explanation: one or two words each, the same words the popover and
  // AVAILABILITY_VIEW use (without their emoji). The longer meaning is the item's hover text.
  legendLabel: 'دليل الألوان',
  legendBooked: 'محجوز',
  legendBookedHint: 'الشريط بلون المنصة',
  legendHeld: 'محجوب داخلياً',
  legendStopSell: 'إيقاف البيع',
  legendUnknown: 'بدون بيانات',
  legendUnknownHint: 'لا توجد بيانات توفر لهذا اليوم',
  legendMismatch: 'تعارض',
  legendMismatchHint: 'حجز على ليلة ما زالت معروضة للبيع، أو حجزان على ليلة واحدة',
  loading: 'جارٍ التحميل',
  refreshing: 'يتم التحديث',
  loadError: 'تعذّر تحميل التقويم.',
  retry: 'إعادة المحاولة',
  refreshError: 'تعذّر التحديث — المعروض آخر نسخة محمّلة.',
  noMatch: 'لا توجد وحدات تطابق الفلاتر.',
  noUnits: 'لا توجد وحدات لهذا الحساب.',
  shown: (count, total) => `عرض ${count} من ${total} وحدة`,
  boardLabel: 'توفر الوحدات حسب الليلة',
  unitColumn: 'الوحدة',
  freeCount: (count) => `${count} متاحة`,
  guestFallback: 'ضيف',
  platformFallback: 'منصة غير محددة',
  mismatch: 'تعارض: يوجد حجز لكن الليلة ما زالت معروضة للبيع، أو حجزان على ليلة واحدة — راجع تقويم الوحدة.',
  relocation: 'يقيم هنا ضيف منقول من وحدة أخرى.',
  publishedPrice: 'السعر المنشور',
  noPrice: 'لا يوجد سعر منشور',
  checkIn: 'الوصول',
  checkOut: 'المغادرة',
  duration: 'المدة',
  buildingFact: 'المبنى',
  bedroomsFact: 'غرف النوم',
  owner: 'المالك',
  openUnit: 'فتح صفحة الوحدة',
  openBooking: 'فتح الحجز',
  openNight: 'فتح تقويم الوحدة على هذا اليوم',
  // Airbnb offers (tasks/ops-calendar-airbnb-offers.md). The offer names, the +15% line, «آخر قراءة»
  // and the stale / override sentences come from the pricing card's words (offersText above).
  offersHeading: 'على Airbnb',
  offersNoneTonight: 'لا توجد خصومات على هذه الليلة',
  offersNotReadYet: 'لم تُقرأ عروض هذه الوحدة من Airbnb بعد.',
  offersNotOnAccount: 'إعلان هذه الوحدة لم يعد ضمن حساب Airbnb في Channex.',
  openPricing: 'فتح صفحة التسعير',
  promoTag: 'عرض',
  promoTagHint: (names) => `عرض مفعّل على Airbnb: ${names}`,
  bookingRange: (from, to) => `من ${from} إلى ${to}`
};

export const STATUS_OPTIONS = [
  ['active', 'نشطة'],
  ['maintenance', 'صيانة'],
  ['inactive', 'غير نشطة'],
  ['all', 'الكل']
];

// Tag beside a unit's code — an active unit carries none.
export const UNIT_STATUS_TAG = { maintenance: 'صيانة', inactive: 'غير نشطة' };

// Values are the API's (`3plus`, `none`); counts read in Arabic-Indic digits, as counts do.
export const BEDROOM_OPTIONS = [
  ['', 'الكل'],
  ['0', 'استوديو'],
  ['1', '١'],
  ['2', '٢'],
  ['3plus', '٣ فأكثر'],
  ['none', 'غير محدد']
];

/** «غرف النوم» for a unit; null when the unit's record leaves it blank. */
export const bedroomsLabel = (n) => {
  if (n === null || n === undefined) return null;
  if (n === 0) return 'استوديو';
  if (n === 1) return 'غرفة نوم';
  if (n === 2) return 'غرفتا نوم';
  return `${n.toLocaleString('ar-SA')} غرف نوم`;
};

/** Length of a stay in Arabic, with the dual and plural forms. */
export const nightsLabel = (n) => {
  if (n === 1) return 'ليلة واحدة';
  if (n === 2) return 'ليلتان';
  const count = n.toLocaleString('ar-SA');
  return n <= 10 ? `${count} ليالٍ` : `${count} ليلة`;
};
